import { ReactElement, useId } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { NewSelectMultiSelect, CustomTooltip } from '../PIM/common';
import { SortIcon, SortDownIcon } from '../../bombiix-icon';

export interface BombiixSortOption {
  label: string;
  /** Document field the list is ordered by. */
  propertyName: string;
  sortOrder: 'Asc' | 'Desc';
}

export interface BombiixSortButtonProps {
  options: BombiixSortOption[];
  /** Currently applied sort, or null when the list is in its default order. */
  value: BombiixSortOption | null;
  onChange: (_option: BombiixSortOption | null) => void;
  /** Popover copy — "Sort by" in the select placeholder, the rest on the trigger. */
  sortByLabel: string;
  ariaLabel: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  disabled?: boolean;
  testId?: string;
}

const ICON_SIZE = 28;

/**
 * Renders the toolbar sort control: an icon button opening a popover of sort options.
 * @param {BombiixSortButtonProps} props - The sort options, applied value and change handler.
 * @returns {ReactElement} The sort icon button wrapped in its popover.
 */
export function BombiixSortButton({
  options,
  value,
  onChange,
  sortByLabel,
  ariaLabel,
  placement = 'bottom',
  disabled = false,
  testId,
}: BombiixSortButtonProps): ReactElement {
  const popoverId = useId();
  const isDescending = value?.sortOrder === 'Desc';

  const popover = (
    <Popover id={popoverId} className="bombiix-sort-popover">
      <Popover.Content className="p-2">
        <NewSelectMultiSelect<BombiixSortOption>
          options={options}
          value={value}
          onChange={(selected) => onChange((selected as BombiixSortOption) ?? null)}
          getOptionValue={(option) => `${option.propertyName}-${option.sortOrder}`}
          placeholder={sortByLabel}
          isClearable
          isSearchable={false}
        />
      </Popover.Content>
    </Popover>
  );

  return (
    <CustomTooltip placement="top" tooltipText={ariaLabel}>
      <button
        type="button"
        aria-label={ariaLabel}
        disabled={disabled}
        className={value ? 'bombiix-icon-container active' : 'bombiix-icon-container'}
        data-testid={testId}
      >
        <OverlayTrigger placement={placement} trigger="click" rootClose overlay={popover}>
          <span className="d-inline-flex">{isDescending ? <SortDownIcon size={ICON_SIZE} /> : <SortIcon size={ICON_SIZE} />}</span>
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default BombiixSortButton;
